import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import type { AppCategory } from '@bliks-io/contracts';
import {
  Badge,
  Card,
  Item,
  ItemContent,
  ItemMedia,
  ItemTitle,
  Label,
  Separator,
  Skeleton,
  Switch,
  cn
} from '@bliks-io/ui';
import { appCategoryLabel } from '@/lib/app-registry';
import { resolveAppIcon } from '@/lib/app-icon-resolver';
import { resolveAppTitle } from '@/lib/app-loader';
import {
  appConsoleStatus,
  type AppConsoleStatus,
  type MergedAppRow
} from '@/lib/merge-app-catalog';

export function CategoryBadge({ category }: { category?: AppCategory }) {
  const { t } = useTranslation();
  const label = category && appCategoryLabel(category, t);
  if (!label) return null;
  return (
    <Badge variant="secondary" size="sm">
      {label}
    </Badge>
  );
}

const STATUS_LABEL_KEYS: Record<AppConsoleStatus, string> = {
  enabled: 'common.Apps.enabled',
  disabled: 'common.Apps.disabled',
  notAllowed: 'common.Apps.notAllowed'
};

const STATUS_VARIANTS: Record<AppConsoleStatus, 'default' | 'secondary' | 'outline'> = {
  enabled: 'default',
  disabled: 'secondary',
  notAllowed: 'outline'
};

export function AppStatusBadge({ status }: { status: AppConsoleStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant={STATUS_VARIANTS[status]} size="sm">
      {t(STATUS_LABEL_KEYS[status])}
    </Badge>
  );
}

export function IconTile({
  icon: Icon,
  status,
  size
}: {
  icon: LucideIcon;
  status: AppConsoleStatus;
  size: 'sm' | 'md';
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg',
        size === 'md' ? 'h-11 w-11' : 'h-9 w-9',
        status === 'enabled'
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground'
      )}
    >
      <Icon className={size === 'md' ? 'h-5 w-5' : 'h-4 w-4'} />
    </div>
  );
}

/**
 * The detail header's control: a switch named by a visible label, wired to it by
 * an id derived from the app so several app panels cannot collide. The label
 * names the control and never the state — a label that flipped with the switch
 * would take the accessible name with it, leaving the control unaddressable.
 */
export function AppDetailSwitch({
  appId,
  label,
  checked,
  disabled,
  onCheckedChange
}: {
  appId: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: () => void;
}) {
  const id = `app-console-switch-${appId}`;
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  );
}

/**
 * The layout both app surfaces share: the tenant's apps on the left, the
 * selected app's role-specific panel on the right. The panel flows with the
 * page instead of capping its own height, so a tall configuration block cannot
 * be clipped.
 */
export function AppMasterDetail({
  rows,
  emptyMessage,
  isLoading,
  railAction,
  renderHeaderAction,
  renderDetail
}: {
  rows: MergedAppRow[];
  emptyMessage: string;
  isLoading?: boolean;
  /** Sits above the list, inside the sticky column — e.g. the add-app action. */
  railAction?: ReactNode;
  renderHeaderAction?: (app: MergedAppRow) => ReactNode;
  renderDetail: (app: MergedAppRow) => ReactNode;
}) {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [selectedId, setSelectedId] = useState<string>();

  // Dropped as soon as the row is gone, not merely fallen back from: a stale id
  // kept around would silently steal the selection back the moment the same app
  // is added again.
  const match = rows.find((app) => app.appId === selectedId);
  if (selectedId && !match) setSelectedId(undefined);

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const selected = match ?? rows[0];
  const detail = selected && renderDetail(selected);

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[320px_1fr]">
      {/* Sticky sits on the grid ITEM: `items-start` shrinks it to its content
          while its containing block stays the full-height grid row, which is the
          scroll range the sticky needs. `PageContent` is the scrollport and the
          page header lives outside it, so `top-0` clears nothing to offset. */}
      <div className="space-y-3 lg:sticky lg:top-0 lg:max-h-[75vh] lg:overflow-y-auto">
        {railAction}
        <Card className="divide-y overflow-hidden p-0">
          {rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">{emptyMessage}</p>
          ) : (
            rows.map((app) => {
              const status = appConsoleStatus(app);
              const isSelected = app.appId === selected?.appId;
              const title = resolveAppTitle(app.appId, app.name, locale);
              return (
                <Item
                  key={app.appId}
                  asChild
                  size="sm"
                  className={cn(
                    'w-full rounded-none text-left hover:bg-muted/60',
                    isSelected && 'bg-muted'
                  )}
                >
                  <button
                    type="button"
                    aria-label={title}
                    aria-current={isSelected}
                    onClick={() => setSelectedId(app.appId)}
                  >
                    <ItemMedia>
                      <IconTile icon={resolveAppIcon(app.iconName)} status={status} size="sm" />
                    </ItemMedia>
                    <ItemContent className="min-w-0">
                      <ItemTitle className="block w-full truncate">{title}</ItemTitle>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <AppStatusBadge status={status} />
                        <CategoryBadge category={app.category} />
                      </div>
                    </ItemContent>
                  </button>
                </Item>
              );
            })
          )}
        </Card>
      </div>

      {selected && (
        <Card className="space-y-6 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <IconTile
                icon={resolveAppIcon(selected.iconName)}
                status={appConsoleStatus(selected)}
                size="md"
              />
              <div className="space-y-1">
                <div className="text-lg font-semibold leading-tight">
                  {resolveAppTitle(selected.appId, selected.name, locale)}
                </div>
                <CategoryBadge category={selected.category} />
                {selected.description && (
                  <p className="max-w-2xl pt-1 text-sm leading-relaxed text-muted-foreground">
                    {selected.description}
                  </p>
                )}
              </div>
            </div>
            {renderHeaderAction?.(selected)}
          </div>

          {detail && (
            <>
              <Separator />

              {/* Remounts the panel per app so a pending seat draft cannot leak across a selection change. */}
              <div key={selected.appId}>{detail}</div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
